// src/components/HazardItem.jsx

import React, { useState, memo } from 'react';
import {
  Box,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  ImageList,
  ImageListItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'; // Icon for expanding
import DeleteIcon from '@mui/icons-material/Delete'; // Icon for deleting images and hazards
import PropTypes from 'prop-types';
import ImageUpload from './ImageUpload'; // Import the ImageUpload component

const HazardItem = ({
  hazard,
  isEditMode,
  onInputChange,
  onEditToggle,
  onRemoveImage,
  onDelete, // New prop for handling deletion
  index,
  riskLevels,
  frequencyLevels,
  saving, // New prop for handling the saving state
}) => {
  const [isExpanded, setIsExpanded] = useState(false); // Local state for expansion
  const [confirmDelete, setConfirmDelete] = useState(false); // State for confirmation dialog

  const images = hazard.media?.images || [];
  const permissions = hazard.media?.permissions || [];

  const handleRemoveUploadedImage = (imgIndex) => {
    onRemoveImage(index, imgIndex);
  };

  const handleExpandClick = () => {
    setIsExpanded((prev) => !prev);
  };

  const handleConfirmDelete = () => {
    onDelete(index);
    setConfirmDelete(false);
  };

  return (
    <Accordion expanded={isExpanded} onChange={handleExpandClick}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel-content-${index}`}
        id={`panel-header-${index}`}
      >
        <Box width="100%">
          <Grid container spacing={1} alignItems="center">
            {/* Area - Smaller Column */}
            <Grid item xs={12} sm={2}>
              <Typography
                variant="subtitle1"
                sx={{ wordBreak: 'break-word' }}
              >
                <strong>Area:</strong> {hazard.area || 'N/A'}
              </Typography>
            </Grid>
            {/* Hazard - Bigger Column */}
            <Grid item xs={12} sm={6}>
              <Typography
                variant="subtitle1"
                sx={{ wordBreak: 'break-word' }}
              >
                <strong>Hazard:</strong> {hazard.hazard || 'N/A'}
              </Typography>
            </Grid>
            {/* Reference - Medium Column */}
            <Grid item xs={12} sm={4}>
              <Typography
                variant="subtitle1"
                sx={{ wordBreak: 'break-word' }}
              >
                <strong>Reference:</strong> {hazard.reference || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Hazard Description */}
          <Grid item xs={12}>
            <TextField
              label="Hazard Description"
              value={isEditMode ? hazard.hazard || '' : hazard.hazard || ''}
              onChange={(e) => onInputChange(index, 'hazard', e.target.value)}
              fullWidth
              multiline
              rows={2}
              disabled={!isEditMode || saving}
            />
          </Grid>

          {/* Area */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Area"
              value={isEditMode ? hazard.area || '' : hazard.area || ''}
              onChange={(e) => onInputChange(index, 'area', e.target.value)}
              fullWidth
              disabled={!isEditMode || saving}
            />
          </Grid>

          {/* Reference */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Reference"
              value={isEditMode ? hazard.reference || '' : hazard.reference || ''}
              onChange={(e) => onInputChange(index, 'reference', e.target.value)}
              fullWidth
              disabled={!isEditMode || saving}
            />
          </Grid>

          {/* Type */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Type"
              value={isEditMode ? hazard.type || '' : hazard.type || ''}
              onChange={(e) => onInputChange(index, 'type', e.target.value)}
              fullWidth
              disabled={!isEditMode || saving}
            />
          </Grid>

          {/* Eliminated */}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    isEditMode
                      ? hazard.eliminated !== undefined
                        ? hazard.eliminated
                        : false
                      : hazard.eliminated || false
                  }
                  onChange={(e) => onInputChange(index, 'eliminated', e.target.checked)}
                  disabled={!isEditMode || saving}
                />
              }
              label="Eliminated"
            />
          </Grid>

          {/* Location */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Location"
              value={isEditMode ? hazard.location || '' : hazard.location || ''}
              onChange={(e) => onInputChange(index, 'location', e.target.value)}
              fullWidth
              disabled={!isEditMode || saving}
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={isEditMode ? hazard.status || '' : hazard.status || ''}
                onChange={(e) => onInputChange(index, 'status', e.target.value)}
                disabled={!isEditMode || saving}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Frequency */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Frequency</InputLabel>
              <Select
                value={isEditMode ? hazard.frequency || '' : hazard.frequency || ''}
                onChange={(e) => onInputChange(index, 'frequency', parseInt(e.target.value, 10))}
                disabled={!isEditMode || saving}
              >
                {frequencyLevels.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Initial Rating */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Initial Rating</InputLabel>
              <Select
                value={isEditMode ? hazard.initial_rating || '' : hazard.initial_rating || ''}
                onChange={(e) => onInputChange(index, 'initial_rating', parseInt(e.target.value, 10))}
                disabled={!isEditMode || saving}
              >
                {riskLevels.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Consequence */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Consequence</InputLabel>
              <Select
                value={isEditMode ? hazard.consequence || '' : hazard.consequence || ''}
                onChange={(e) => onInputChange(index, 'consequence', parseInt(e.target.value, 10))}
                disabled={!isEditMode || saving}
              >
                <MenuItem value={1}>Low</MenuItem>
                <MenuItem value={3}>Medium</MenuItem>
                <MenuItem value={5}>High</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Residual Risk Rating */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Residual Risk Rating</InputLabel>
              <Select
                value={isEditMode ? hazard.rating || '' : hazard.rating || ''}
                onChange={(e) => onInputChange(index, 'rating', parseInt(e.target.value, 10))}
                disabled={!isEditMode || saving}
              >
                {riskLevels.map((level) => (
                  <MenuItem key={level.value} value={level.value}>
                    {level.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Controlled */}
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    isEditMode
                      ? hazard.controlled !== undefined
                        ? hazard.controlled
                        : false
                      : hazard.controlled || false
                  }
                  onChange={(e) => onInputChange(index, 'controlled', e.target.checked)}
                  disabled={!isEditMode || saving}
                />
              }
              label="Controlled"
            />
          </Grid>

          {/* Controlled At */}
          {isEditMode && hazard.controlled && (
            <Grid item xs={12} sm={6}>
              <TextField
                label="Controlled At"
                type="datetime-local"
                value={isEditMode ? hazard.controlled_at || '' : hazard.controlled_at || ''}
                onChange={(e) => onInputChange(index, 'controlled_at', e.target.value)}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={!isEditMode || saving}
              />
            </Grid>
          )}

          {/* Measure */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Measure"
              value={isEditMode ? hazard.measure || '' : hazard.measure || ''}
              onChange={(e) => onInputChange(index, 'measure', e.target.value)}
              fullWidth
              disabled={!isEditMode || saving}
            />
          </Grid>

          {/* Corrective Action */}
          <Grid item xs={12}>
            <TextField
              label="Corrective Action"
              value={isEditMode ? hazard.corrective_action || '' : hazard.corrective_action || ''}
              onChange={(e) => onInputChange(index, 'corrective_action', e.target.value)}
              fullWidth
              multiline
              rows={2}
              disabled={!isEditMode || saving}
            />
          </Grid>

          {/* Internal Description */}
          <Grid item xs={12}>
            <TextField
              label="Internal Description"
              value={
                isEditMode
                  ? hazard.internal_description || ''
                  : hazard.internal_description || ''
              }
              onChange={(e) => onInputChange(index, 'internal_description', e.target.value)}
              fullWidth
              multiline
              rows={2}
              disabled={!isEditMode || saving}
            />
          </Grid>
        </Grid>

        {/* Image Upload Section */}
        {isEditMode && (
          <Box marginY={2}>
            <Typography variant="h6" gutterBottom>
              Attached Images
            </Typography>
            <ImageUpload
              docId={hazard.id ? `hazard-${hazard.id}` : `hazard-new-${index}`}
              uploadedImages={images}
              setUploadedImages={(images) => onInputChange(index, 'media.images', images)}
              isEditable={isEditMode && !saving} // Disable editing during saving
              handleRemoveImage={handleRemoveUploadedImage}
            />
          </Box>
        )}

        {/* Image Display Section (Read-Only Mode) */}
        {!isEditMode && images.length > 0 && (
          <Box marginY={2}>
            <Typography variant="h6" gutterBottom>
              Images
            </Typography>
            <ImageList cols={3} gap={8} sx={{ overflow: 'hidden' }}>
              {images.map((imgUrl, imgIndex) => (
                <ImageListItem key={imgIndex}>
                  <img
                    src={imgUrl}
                    alt={`Hazard Image ${imgIndex + 1}`}
                    loading="lazy"
                    style={{ borderRadius: 8, width: '100%', height: 'auto' }}
                  />
                </ImageListItem>
              ))}
            </ImageList>
          </Box>
        )}

        {/* Action Buttons: Edit and Delete */}
        <Box display="flex" justifyContent="flex-end" gap={2} marginTop={2}>
          {hazard.id === undefined && (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setConfirmDelete(true)}
                disabled={!isEditMode || saving} // Disable during saving
              >
                Delete
              </Button>
              {/* Confirmation Dialog */}
              <Dialog
                open={confirmDelete}
                onClose={() => setConfirmDelete(false)}
                aria-labelledby="confirm-delete-dialog-title"
              >
                <DialogTitle id="confirm-delete-dialog-title">Confirm Deletion</DialogTitle>
                <DialogContent>
                  <Typography>Are you sure you want to delete this hazard?</Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setConfirmDelete(false)} color="primary">
                    Cancel
                  </Button>
                  <Button onClick={handleConfirmDelete} color="error">
                    Delete
                  </Button>
                </DialogActions>
              </Dialog>
            </>
          )}
          <Button
            variant="outlined"
            color="primary"
            onClick={() => onEditToggle(index)}
            disabled={saving} // Disable during saving
          >
            {isEditMode ? 'Cancel Edit' : 'Edit'}
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

// Define PropTypes for better type checking
HazardItem.propTypes = {
  hazard: PropTypes.shape({
    id: PropTypes.number, // Existing hazards have an 'id'; new ones do not
    hazard: PropTypes.string,
    area: PropTypes.string,
    reference: PropTypes.string,
    type: PropTypes.string,
    eliminated: PropTypes.bool,
    location: PropTypes.string,
    status: PropTypes.number,
    frequency: PropTypes.number,
    initial_rating: PropTypes.number,
    consequence: PropTypes.number,
    rating: PropTypes.number,
    controlled: PropTypes.bool,
    controlled_at: PropTypes.string, // ISO string for datetime-local
    measure: PropTypes.string,
    corrective_action: PropTypes.string,
    internal_description: PropTypes.string,
    media: PropTypes.shape({
      images: PropTypes.arrayOf(PropTypes.string),
      permissions: PropTypes.arrayOf(PropTypes.bool),
    }),
  }).isRequired,
  isEditMode: PropTypes.bool.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onEditToggle: PropTypes.func.isRequired,
  onRemoveImage: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired, // New prop for handling deletion
  index: PropTypes.number.isRequired,
  riskLevels: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    })
  ).isRequired,
  frequencyLevels: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.number,
    })
  ).isRequired,
  saving: PropTypes.bool.isRequired, // New prop for handling the saving state
};

export default memo(HazardItem);
